// `extension-alert-client-contacts-ats-info` — the "<ATS> information" card on a contact's
// profile. It is the candidate card's twin and it agrees with it on almost nothing.
//
// ⚠️ IT OPENS EXPANDED, UNCONDITIONALLY. The candidate card sets `collapseAtsInfo = inATS`, so
// it opens closed in the common case; this one assigns `false` flat, with no session test at
// all. The reference capture shows COLLAPSE on the contact profile and EXPAND on the candidate
// profile, on the same session — that is not a state difference, it is two different defaults.
//
// ⚠️ AND THE FIELD NAMES DIFFER FOR THE SAME TWO VALUES: `atsInfo.ownerName` / `atsInfo.status`
// here against `ownerFullName` / `latestStatus` on the candidate. Reusing the candidate's DTO
// reads undefined twice and prints "Unknown" twice, which looks like missing data.
import { createElement, useState, type ReactElement } from 'react'
import { displayDate } from '../common/displayDate.ts'
import { TextReadMore } from '../common/TextReadMore.tsx'
import { atsNoteCopy, noteSubtitle, type AtsNote } from './AtsInfoCard.tsx'

/** `IClientContactAtsInfoDto`, as this card reads it. */
export interface ClientContactAtsInfo {
  ownerName?: string | null
  status?: string | null
  contactHighestActivityLevel?: string | null
  contactHighestActivityCount?: number | null
  contactLastActivityName?: string | null
  contactLastActivityDate?: number | null
  /** The ATS's free-text field on the contact; Vincere calls it the "Brief". */
  comments?: string | null
  /** The latest notes, newest first, as the server picked them. */
  lastNotes?: readonly AtsNote[] | null
}

/**
 * `ATSClientContactCommentLabelCopyOptions` / `…Default` (`shortlist.constants.ng1.ts:205`): the
 * label over `atsInfo.comments`. Its own table, not the note table's: Vincere's contact field
 * is the "Brief" while its notes are "comments".
 */
const ATS_COMMENTS_LABEL: Readonly<Record<string, string>> = { Bullhorn: 'Comments', Vincere: 'Brief' }
const ATS_COMMENTS_LABEL_DEFAULT = 'Comments'

export function contactCommentsLabel(atsName: string | undefined): string {
  if (atsName === undefined) return ATS_COMMENTS_LABEL_DEFAULT
  return ATS_COMMENTS_LABEL[atsName] ?? ATS_COMMENTS_LABEL_DEFAULT
}

/**
 * `getHighestActivityText` — "Had placements (2 in total)".
 *
 * ⚠️ THIS IS NOT `previousEngagementText`, AND THE TWO ARE A METRE APART IN THIS FOLDER. The
 * contact ROW rewrites the same two fields into "2 placements", dropping the level's wording;
 * the CARD keeps the level verbatim and appends the count in a parenthetical. Same data, same
 * screen family, two formats — so reusing the row's helper here prints the row's string on the
 * card and reads as correct until someone compares it with production.
 */
export function highestActivityText(level: string, count: number): string {
  return `${level} (${String(count)} in total)`
}

/** `getLastActivityText` — the date is appended only when there is one. */
export function lastActivityText(name: string, date?: number | null): string {
  const when = displayDate(date)
  return when === undefined ? `Last activity: ${name}` : `Last activity: ${name}, ${when}`
}

export interface ClientContactAtsInfoCardProps {
  atsInfo: ClientContactAtsInfo
  atsName?: string
  /**
   * `getAtsLabel($window)` — the ATS the card NAMES (5cfd3ef899). `atsName` stays the key of the
   * note-copy table. Defaults to `atsName`.
   */
  atsLabel?: string
  /**
   * `(!inATS) || (featureFlags.CREATE_CLIENT_CONTACT_IN_ATS && careero.createClientContactInAtsEnabled)`
   *
   * ⚠️ THE SECOND HALF HAS NO CANDIDATE EQUIVALENT. A tenant that can CREATE contacts in its
   * ATS shows the header's "View in ATS" even while the user is already inside the ATS —
   * because the record may not be there yet. Porting only the `!inATS` half hides the link on
   * exactly the tenants that need it.
   */
  showViewInAts?: boolean
  onViewInAts?: () => void
}

export function ClientContactAtsInfoCard({
  atsInfo,
  atsName,
  atsLabel = atsName,
  showViewInAts = false,
  onViewInAts,
}: ClientContactAtsInfoCardProps): ReactElement {
  // Not `useState(inAts)` — see the note at the top. This card has no session input at all.
  const [collapsed, setCollapsed] = useState(false)

  const level = atsInfo.contactHighestActivityLevel
  const count = atsInfo.contactHighestActivityCount
  const hasHighest = level !== undefined && level !== null && level !== '' && count !== undefined && count !== null && count !== 0
  // `!!ctrl.atsInfo.lastNotes`: present at all, as upstream tests it. An empty array is truthy
  // there and draws the title over no notes, so it does here too.
  const notes = atsInfo.lastNotes ?? undefined
  const lastName = atsInfo.contactLastActivityName
  const hasLast = lastName !== undefined && lastName !== null && lastName !== ''

  const content = (
    <div className="extension-candidates-ats-info-content">
      <div className="snippet-section">
        <div className="snippet-section-header ats-info-header">
          <div>
            <strong>{atsLabel} information</strong>
          </div>
          {!showViewInAts ? null : (
            <div className="ats-info-header-view-in-ats">
              <span
                role="button"
                tabIndex={0}
                onClick={onViewInAts}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') onViewInAts?.()
                }}
              >
                View in ATS
              </span>
            </div>
          )}
        </div>
        <div className="snippet-section-body ats-info-body">
          <div className={`ats-info-collapsible-container${collapsed ? ' collapse-ats-info' : ''}`}>
            <div className="ats-info-details">
              <div className="ats-info-details-row">
                <div className="ats-info-details-cell">
                  {/* ⚠️ OWNER AND STATUS SHARE ONE CELL HERE, nested inside `.owner-status-cell`,
                      where the candidate card gives each its own `.ats-info-details-cell` in a
                      two-column row. The contact card's second row is the full-width Previous
                      engagement, so the grid could not be reused. */}
                  <div className="owner-status-cell">
                    <div>
                      <div className="ats-info-details-title">Owner</div>
                      <div className="ats-info-details-text ats-info-owner-name">
                        {atsInfo.ownerName === undefined || atsInfo.ownerName === null || atsInfo.ownerName === ''
                          ? 'Unknown'
                          : atsInfo.ownerName}
                      </div>
                    </div>
                    <div className="contact-status">
                      <div className="ats-info-details-title">Status</div>
                      <div className="ats-info-details-text ats-info-latest-status">
                        {atsInfo.status === undefined || atsInfo.status === null || atsInfo.status === ''
                          ? 'Unknown'
                          : atsInfo.status}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="ats-info-details-row">
                <div className="ats-info-details-cell previous-engagement-cell">
                  <div className="ats-info-details-title">Previous engagement</div>
                  {!hasHighest ? null : (
                    <div className="ats-info-details-text ats-info-details-subtitle">
                      {highestActivityText(level, count)}
                    </div>
                  )}
                  {!hasLast ? null : (
                    <div className="ats-info-details-text">
                      {lastActivityText(lastName, atsInfo.contactLastActivityDate)}
                    </div>
                  )}
                  {/* ⚠️ "None" NEEDS **BOTH** BLOCKS ABSENT, not either. A contact with a last
                      activity but no highest level shows that line and no "None" — the three
                      conditions are not a switch. */}
                  {hasHighest || hasLast ? null : <div className="ats-info-details-text">None</div>}
                </div>
              </div>
              {/* `ng-if="!!ctrl.atsInfo.comments"`. Found missing 28 Oct by the re-captured
                  reference: a Vincere contact's "Brief" was absent from this card. */}
              {atsInfo.comments === undefined || atsInfo.comments === null || atsInfo.comments === '' ? null : (
                <div className="ats-info-details-row">
                  <div className="ats-info-details-cell comments-cell">
                    <div className="ats-info-details-title">{contactCommentsLabel(atsName)}</div>
                    <div className="ats-info-details-text">{atsInfo.comments}</div>
                  </div>
                </div>
              )}
            </div>
            {/* `ng-if="::!!ctrl.atsInfo.lastNotes"`: the title ONCE, pluralised on the count,
                then a subtitle and a read-more per note. The candidate card shows a single note
                with its own title, so its block cannot be reused as it stands. */}
            {notes === undefined ? null : (
              <div className="ats-info-notes-container">
                <div className="ats-info-note-title">
                  Latest {atsNoteCopy(atsName)}
                  {notes.length === 1 ? '' : 's'}
                </div>
                {notes.map((note, index) => (
                  <div className="ats-info-note" key={index}>
                    <div className="ats-info-note-subtitle">{noteSubtitle(note)}</div>
                    <div className="ats-info-note-content">
                      {/* `display-as-html="false"`: this is text a recruiter typed into the ATS. */}
                      <TextReadMore text={note.text} lineLimit={5} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="view-more-less-cont">
            <span
              className={`view-more-less ${collapsed ? 'view-more' : 'view-less'}`}
              role="button"
              tabIndex={0}
              onClick={() => {
                setCollapsed(!collapsed)
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') setCollapsed(!collapsed)
              }}
            >
              {collapsed ? 'Expand ' : 'Collapse '}
              <i className={collapsed ? 'fas fa-angle-down' : 'fas fa-angle-up'} />
            </span>
          </div>
        </div>
      </div>
    </div>
  )

  // ⚠️ THE ELEMENT CARRIES THIS CARD'S ENTIRE STYLESHEET, and the candidate card's does not —
  // that one is all classes. `extension-alert-client-contacts-ats-info { .owner-status-cell {…}
  // .previous-engagement-cell {…} }`, so a plain div stacks Owner above Status where production
  // sets them side by side. Eleventh element type in this port to hold the rules.
  return createElement('extension-alert-client-contacts-ats-info', null, content)
}
